import { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
} from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db, appId } from '@/lib/firebase';
import { Avatar } from './ui/Avatar';

interface CommentsSectionProps {
  reportId: string;
  currentUser: FirebaseUser | null;
  userProfile: { displayName: string; photoUrl?: string | null } | null;
  onLoginReq: () => void;
}

interface Comment {
  id: string;
  text: string;
  authorName: string;
  authorPhoto?: string | null;
  createdAt: any;
}

export const CommentsSection = ({
  reportId,
  currentUser,
  userProfile,
  onLoginReq,
}: CommentsSectionProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  const unsubRef = useRef<(() => void) | null>(null);
  const lastCountRef = useRef(-1);
  const activeRef = useRef(false);
  
  useEffect(() => {
    if (!reportId) return;
    
    // Guard: Skip if already active
    if (activeRef.current) return;
    
    let isMounted = true;
    activeRef.current = true; // Mark as active
    
    const q = query(
      collection(
        db,
        'artifacts',
        appId,
        'public',
        'data',
        `report_${reportId}_comments`
      ),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    
    unsubRef.current = onSnapshot(q, (snapshot) => {
      if (!isMounted) return;
      
      const commentsList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Comment));
      setComments(commentsList);
      
      // Only update comments count if it changed to avoid unnecessary writes
      const currentCount = snapshot.size;
      if (currentCount !== lastCountRef.current) {
        lastCountRef.current = currentCount;
        const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', reportId);
        updateDoc(reportRef, {
          'communitySignals.commentsCount': currentCount,
        }).catch((error) => {
          console.error('[CommentsSection] Error updating comments count:', error);
        });
      }
    }, (error) => {
      if (!isMounted) return;
      console.error('[CommentsSection] Error listening to comments:', error);
    });
    
    return () => {
      isMounted = false;
      activeRef.current = false; // Reset on cleanup
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [reportId]);

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) {
      onLoginReq();
      return;
    }
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      await addDoc(
        collection(
          db,
          'artifacts',
          appId,
          'public',
          'data',
          `report_${reportId}_comments`
        ),
        {
          text: newComment,
          userId: currentUser?.uid,
          authorName: userProfile.displayName,
          authorPhoto: userProfile.photoUrl || null,
          createdAt: serverTimestamp(),
        }
      );
      
      // Increment comments count
      const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', reportId);
      await updateDoc(reportRef, {
        'communitySignals.commentsCount': increment(1),
      });
      
      setNewComment('');
    } catch (err) {
      console.error('[CommentsSection] Comment failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 p-4 border-t border-gray-100">
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
        <MessageSquare size={14} />
        Discussion ({comments.length})
      </h4>

      <div className="space-y-3 mb-4 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
        {comments.length === 0 && (
          <p className="text-sm text-gray-400 italic">No comments yet.</p>
        )}
        {comments.map((c) => (
          <div key={c.id} className="flex gap-2 items-start">
            <Avatar
              name={c.authorName}
              url={c.authorPhoto}
              size="xs"
              className="mt-1 shrink-0"
            />
            <div className="bg-white p-2 rounded-lg rounded-tl-none shadow-sm text-sm border border-gray-100 flex-1">
              <span className="font-bold text-gray-800 mr-2">
                {c.authorName}
              </span>
              <span className="text-gray-600">{c.text}</span>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handlePost} className="flex gap-2">
        <input
          type="text"
          placeholder={userProfile ? 'Add a comment...' : 'Sign in to comment'}
          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={!userProfile}
        />
        <button
          disabled={!newComment.trim() || loading}
          className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};
